import { useEffect } from 'react';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

export default function TermsConditions() {
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Schema.org JSON-LD for Terms and Conditions page
    const schema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Términos y Condiciones - Zhongming Import SL",
      "description": "Términos y condiciones de uso del sitio web y servicios B2B de Zhongming Import SL. Condiciones comerciales para profesionales del sector construcción.",
      "url": `${import.meta.env.VITE_SITE_URL || "https://zhongmingimport.com"}/terms-conditions`,
      "inLanguage": "es",
      "isPartOf": {
        "@type": "WebSite",
        "name": "Zhongming Import SL",
        "url": import.meta.env.VITE_SITE_URL || "https://zhongmingimport.com"
      },
      "about": {
        "@type": "Organization",
        "name": "Zhongming Import SL",
        "legalName": "Zhongming Import SL",
        "taxID": "B75817635"
      },
      "dateModified": "2024-01-15",
      "datePublished": "2024-01-15"
    };

    const existingScript = document.querySelector('script[type="application/ld+json"]');
    if (existingScript) {
      existingScript.remove();
    }

    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.textContent = JSON.stringify(schema);
    document.head.appendChild(schemaScript);

    return () => {
      const scriptToRemove = document.querySelector('script[type="application/ld+json"]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-32 pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <header>
            <h1 className="text-4xl md:text-5xl font-bold text-blue-900 mb-6">
              Términos y Condiciones
            </h1>
          </header>
          
          <div className="text-gray-600 mb-8">
            <p className="mb-2"><strong>Última actualización:</strong> 15 de enero de 2024</p>
            <p><strong>Empresa:</strong> Zhongming Import SL</p>
            <p><strong>CIF:</strong> B75817635</p>
          </div>

          <div className="prose prose-lg max-w-none">
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">1. Información General</h2>
              <div className="bg-blue-50 p-6 rounded-lg mb-4">
                <p className="mb-2"><strong>Denominación Social:</strong> Zhongming Import SL</p>
                <p className="mb-2"><strong>CIF:</strong> B75817635</p>
                <p className="mb-2"><strong>Domicilio Social:</strong> C/ PICO DE LA CIERVA 1, 28031 - MADRID</p>
                <p className="mb-2"><strong>Teléfono:</strong> +34 688 06 61 41</p>
                <p className="mb-2"><strong>Email:</strong> info@zhongmingimport.com</p>
                <p><strong>Sitio Web:</strong> www.zhongmingimport.com</p>
              </div>
              <p className="text-gray-700 leading-relaxed">
                Los presentes Términos y Condiciones regulan el uso del sitio web www.zhongmingimport.com y los servicios profesionales B2B de importación de materiales de construcción y decoración ofrecidos por Zhongming Import SL. El acceso y uso de este sitio web implica la aceptación plena y sin reservas de estos términos.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">2. Objeto y Ámbito de Aplicación</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Zhongming Import SL es una empresa especializada en la importación directa de materiales de construcción y decoración premium desde China, ofreciendo servicios exclusivamente a profesionales del sector (B2B):
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-building-4-line text-blue-900 mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span>Arquitectos y estudios de arquitectura</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-building-4-line text-blue-900 mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span>Interioristas y diseñadores</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-building-4-line text-blue-900 mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span>Constructoras y promotoras</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-building-4-line text-blue-900 mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span>Mayoristas y distribuidores del sector</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-building-4-line text-blue-900 mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span>Empresas de reformas y rehabilitación</span>
                </li>
              </ul>
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-5 rounded-lg mt-4">
                <p className="text-gray-700">
                  <i className="ri-alert-line text-yellow-600 mr-2"></i>
                  <strong>Importante:</strong> Nuestros servicios están dirigidos exclusivamente a profesionales y empresas del sector. No realizamos ventas directas a consumidores finales.
                </p>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">3. Servicios Ofrecidos</h2>
              <div className="space-y-4">
                <div className="bg-gray-50 p-5 rounded-lg">
                  <h3 className="font-bold text-blue-900 mb-2 flex items-center">
                    <i className="ri-ship-line mr-2 w-5 h-5 flex items-center justify-center"></i>
                    Importación Directa
                  </h3>
                  <p className="text-gray-700">Gestión completa de importación de materiales de construcción desde fabricantes certificados en China, eliminando intermediarios y optimizando costes.</p>
                </div>
                <div className="bg-gray-50 p-5 rounded-lg">
                  <h3 className="font-bold text-blue-900 mb-2 flex items-center">
                    <i className="ri-shield-check-line mr-2 w-5 h-5 flex items-center justify-center"></i>
                    Control de Calidad
                  </h3>
                  <p className="text-gray-700">Inspección y verificación de calidad en origen a través de nuestra oficina en Guangzhou, garantizando el cumplimiento de normativas europeas y certificaciones CE.</p>
                </div>
                <div className="bg-gray-50 p-5 rounded-lg">
                  <h3 className="font-bold text-blue-900 mb-2 flex items-center">
                    <i className="ri-truck-line mr-2 w-5 h-5 flex items-center justify-center"></i>
                    Logística Integral
                  </h3>
                  <p className="text-gray-700">Gestión completa de transporte marítimo/aéreo, despacho aduanero, almacenaje y distribución en España.</p>
                </div>
                <div className="bg-gray-50 p-5 rounded-lg">
                  <h3 className="font-bold text-blue-900 mb-2 flex items-center">
                    <i className="ri-user-settings-line mr-2 w-5 h-5 flex items-center justify-center"></i>
                    Asesoramiento Técnico
                  </h3>
                  <p className="text-gray-700">Consultoría especializada en selección de materiales, especificaciones técnicas y soluciones personalizadas para cada proyecto.</p>
                </div>
                <div className="bg-gray-50 p-5 rounded-lg">
                  <h3 className="font-bold text-blue-900 mb-2 flex items-center">
                    <i className="ri-file-list-3-line mr-2 w-5 h-5 flex items-center justify-center"></i>
                    Documentación y Certificaciones
                  </h3>
                  <p className="text-gray-700">Gestión de toda la documentación técnica, certificados CE, fichas técnicas y declaraciones de conformidad necesarias.</p>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">4. Condiciones de Uso del Sitio Web</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                El usuario se compromete a utilizar el sitio web de forma lícita y conforme a estos términos:
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-red-600 mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span>No realizar actividades ilícitas o contrarias a la buena fe</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-red-600 mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span>No difundir contenidos violentos, discriminatorios o que vulneren derechos de terceros</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-red-600 mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span>No introducir virus, malware o cualquier código dañino</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-red-600 mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span>No intentar acceder a áreas restringidas del sistema</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-red-600 mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span>No utilizar la información del sitio con fines comerciales sin autorización</span>
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">5. Proceso de Contratación B2B</h2>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-10 h-10 flex items-center justify-center bg-blue-900 text-white rounded-full mr-4 flex-shrink-0 font-bold">1</div>
                  <div>
                    <h3 className="font-bold text-blue-900 mb-2">Solicitud de Información</h3>
                    <p className="text-gray-700">El cliente profesional envía una solicitud a través del formulario web o contacto directo, especificando necesidades del proyecto.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 flex items-center justify-center bg-blue-900 text-white rounded-full mr-4 flex-shrink-0 font-bold">2</div>
                  <div>
                    <h3 className="font-bold text-blue-900 mb-2">Análisis y Presupuesto</h3>
                    <p className="text-gray-700">Nuestro equipo analiza los requerimientos y elabora un presupuesto detallado con especificaciones técnicas, plazos y condiciones.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 flex items-center justify-center bg-blue-900 text-white rounded-full mr-4 flex-shrink-0 font-bold">3</div>
                  <div>
                    <h3 className="font-bold text-blue-900 mb-2">Negociación y Acuerdo</h3>
                    <p className="text-gray-700">Se negocian condiciones comerciales y se formaliza el acuerdo mediante contrato mercantil B2B.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 flex items-center justify-center bg-blue-900 text-white rounded-full mr-4 flex-shrink-0 font-bold">4</div>
                  <div>
                    <h3 className="font-bold text-blue-900 mb-2">Pedido y Producción</h3>
                    <p className="text-gray-700">Confirmación del pedido, pago según condiciones acordadas e inicio del proceso de fabricación con control de calidad.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 flex items-center justify-center bg-blue-900 text-white rounded-full mr-4 flex-shrink-0 font-bold">5</div>
                  <div>
                    <h3 className="font-bold text-blue-900 mb-2">Logística y Entrega</h3>
                    <p className="text-gray-700">Gestión de transporte internacional, despacho aduanero y entrega en destino según condiciones pactadas.</p>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">6. Precios y Condiciones de Pago</h2>
              <div className="bg-blue-50 p-6 rounded-lg mb-4">
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-blue-900 mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                    <span>Los precios se establecen en cada presupuesto personalizado y pueden variar según volumen, especificaciones y condiciones de entrega</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-blue-900 mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                    <span>Todos los precios se expresan en euros (€) y no incluyen IVA salvo indicación contraria</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-blue-900 mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                    <span>Las condiciones de pago se negocian individualmente según el proyecto (anticipo, contra entrega, plazos, etc.)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-blue-900 mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                    <span>Los precios pueden estar sujetos a variaciones por fluctuaciones de divisa, costes de transporte o aranceles</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-blue-900 mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                    <span>Métodos de pago: transferencia bancaria, confirming o según acuerdo comercial</span>
                  </li>
                </ul>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">7. Plazos de Entrega</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Los plazos de entrega se especifican en cada presupuesto y dependen de diversos factores:
              </p>
              <ul className="space-y-3 text-gray-700 mb-4">
                <li className="flex items-start">
                  <i className="ri-time-line text-blue-900 mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span>Tiempo de fabricación en origen</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-time-line text-blue-900 mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span>Transporte internacional (marítimo 30-45 días / aéreo 7-15 días)</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-time-line text-blue-900 mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span>Despacho aduanero y trámites administrativos</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-time-line text-blue-900 mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span>Distribución nacional</span>
                </li>
              </ul>
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-5 rounded-lg">
                <p className="text-gray-700">
                  <i className="ri-alert-line text-yellow-600 mr-2"></i>
                  Los plazos son estimados y pueden verse afectados por circunstancias extraordinarias (condiciones meteorológicas, huelgas, problemas aduaneros, etc.). Zhongming Import SL no se hace responsable de retrasos causados por terceros o fuerza mayor.
                </p>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">8. Garantías y Devoluciones</h2>
              <div className="space-y-4">
                <div className="bg-gray-50 p-5 rounded-lg">
                  <h3 className="font-bold text-blue-900 mb-2">Garantía de Calidad</h3>
                  <p className="text-gray-700 mb-3">Todos los materiales cuentan con:</p>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <i className="ri-check-line text-green-600 mr-2 mt-1 w-4 h-4 flex items-center justify-center"></i>
                      <span>Certificación CE y cumplimiento normativa europea</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-green-600 mr-2 mt-1 w-4 h-4 flex items-center justify-center"></i>
                      <span>Inspección de calidad en origen</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-green-600 mr-2 mt-1 w-4 h-4 flex items-center justify-center"></i>
                      <span>Garantía del fabricante según producto</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-green-600 mr-2 mt-1 w-4 h-4 flex items-center justify-center"></i>
                      <span>Documentación técnica completa</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-gray-50 p-5 rounded-lg">
                  <h3 className="font-bold text-blue-900 mb-2">Política de Devoluciones B2B</h3>
                  <p className="text-gray-700 mb-3">Dada la naturaleza profesional de nuestros servicios:</p>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-blue-900 mr-2 mt-1 w-4 h-4 flex items-center justify-center"></i>
                      <span>Los pedidos se realizan bajo especificaciones técnicas acordadas</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-blue-900 mr-2 mt-1 w-4 h-4 flex items-center justify-center"></i>
                      <span>No se aceptan devoluciones por cambio de opinión o error del cliente</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-blue-900 mr-2 mt-1 w-4 h-4 flex items-center justify-center"></i>
                      <span>Se aceptan reclamaciones por defectos de fabricación o no conformidad en 7 días desde recepción</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-arrow-right-s-line text-blue-900 mr-2 mt-1 w-4 h-4 flex items-center justify-center"></i>
                      <span>Las reclamaciones deben documentarse con fotografías y descripción detallada</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">9. Propiedad Intelectual</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Todos los contenidos del sitio web (textos, imágenes, logotipos, diseños, estructura, etc.) son propiedad de Zhongming Import SL o de terceros que han autorizado su uso, y están protegidos por derechos de propiedad intelectual e industrial.
              </p>
              <div className="bg-red-50 border-l-4 border-red-500 p-5 rounded-lg">
                <p className="text-gray-700">
                  <strong>Queda prohibido:</strong> Reproducir, distribuir, comunicar públicamente, transformar o modificar los contenidos sin autorización expresa de Zhongming Import SL, salvo para uso personal y privado.
                </p>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">10. Responsabilidad y Limitaciones</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Zhongming Import SL se compromete a ofrecer servicios profesionales de máxima calidad, no obstante:
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-information-line text-blue-900 mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span>No garantiza la disponibilidad continua del sitio web ni se responsabiliza de fallos técnicos</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-information-line text-blue-900 mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span>No se responsabiliza de daños derivados del uso indebido de los materiales suministrados</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-information-line text-blue-900 mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span>No se responsabiliza de retrasos causados por terceros, aduanas o fuerza mayor</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-information-line text-blue-900 mr-3 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span>La responsabilidad se limita al valor de los materiales suministrados según contrato</span>
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">11. Protección de Datos</h2>
              <p className="text-gray-700 leading-relaxed">
                El tratamiento de datos personales se rige por nuestra <a href="/privacy-policy" className="text-blue-900 font-semibold hover:underline cursor-pointer">Política de Privacidad</a>, cumpliendo con el RGPD y la LOPDGDD. Los datos proporcionados serán tratados con la máxima confidencialidad y seguridad.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">12. Modificaciones</h2>
              <p className="text-gray-700 leading-relaxed">
                Zhongming Import SL se reserva el derecho de modificar estos Términos y Condiciones en cualquier momento. Las modificaciones entrarán en vigor desde su publicación en el sitio web. Se recomienda revisar periódicamente esta página.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">13. Legislación Aplicable y Jurisdicción</h2>
              <div className="bg-blue-50 p-6 rounded-lg">
                <p className="text-gray-700 leading-relaxed mb-4">
                  Estos Términos y Condiciones se rigen por la legislación española. Para la resolución de cualquier controversia derivada de la interpretación o ejecución de estos términos, las partes se someten expresamente a los Juzgados y Tribunales de Madrid, renunciando a cualquier otro fuero que pudiera corresponderles.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  En caso de relaciones B2B con empresas de otros países de la Unión Europea, se aplicará la normativa comunitaria correspondiente y los convenios internacionales ratificados por España.
                </p>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">14. Contacto</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Para cualquier consulta sobre estos Términos y Condiciones, puede contactar con nosotros:
              </p>
              <div className="bg-blue-900 text-white p-6 rounded-lg">
                <p className="mb-2"><strong>Zhongming Import SL</strong></p>
                <p className="mb-2">C/ PICO DE LA CIERVA 1</p>
                <p className="mb-2">28031 - MADRID</p>
                <p className="mb-2">Teléfono: +34 688 06 61 41</p>
                <p className="mb-2">Email: info@zhongmingimport.com</p>
                <p className="text-sm text-blue-100 mt-4">Horario de atención: Lunes a Viernes de 9:00 a 18:00</p>
              </div>
            </section>

            <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-lg">
              <p className="text-gray-700 leading-relaxed">
                <i className="ri-check-double-line text-green-600 mr-2"></i>
                <strong>Al utilizar nuestro sitio web y servicios, usted acepta estos Términos y Condiciones en su totalidad.</strong> Si no está de acuerdo con alguna parte de estos términos, le rogamos que no utilice nuestros servicios.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
